import { useConvexConnectionState } from "convex/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Network status states derived from browser and Convex connection signals.
 *
 * State machine:
 * - loading: First load, never connected to Convex (browser online)
 * - connected: Normal operation (browser online AND Convex connected)
 * - offline: Browser reports no network connectivity
 * - reconnecting: Browser online but Convex WebSocket disconnected (and has connected before)
 */
export type NetworkStatus = "loading" | "connected" | "offline" | "reconnecting";

export interface NetworkStatusState {
  /** Derived network status based on browser and Convex signals */
  status: NetworkStatus;
  /** Whether browser reports network connectivity */
  isOnline: boolean;
  /** Whether Convex WebSocket is connected */
  isConvexConnected: boolean;
  /** Whether Convex has ever successfully connected (distinguishes first load from reconnection) */
  hasEverConnected: boolean;
  /** Number of Convex reconnection attempts */
  connectionRetries: number;
}

// Debounce delays (ms)
const ONLINE_DEBOUNCE_MS = 300; // Longer debounce for online - confirm stable connection
const OFFLINE_DEBOUNCE_MS = 100; // Shorter debounce for offline - more urgent

/**
 * useNetworkStatus Hook
 *
 * Combines browser offline detection (navigator.onLine) with Convex connection
 * state to provide accurate network status for UI feedback.
 *
 * Features:
 * - Hybrid detection: Browser API for fast offline detection, Convex for backend status
 * - Debouncing: Prevents UI flicker on rapid network changes
 * - State machine: Distinguishes loading, connected, offline, and reconnecting states
 *
 * @example
 * ```typescript
 * const { status, isOnline, connectionRetries } = useNetworkStatus();
 *
 * if (status === "offline") {
 *   return <OfflineBanner />;
 * }
 * if (status === "reconnecting") {
 *   return <ReconnectingBanner retries={connectionRetries} />;
 * }
 * ```
 */
export function useNetworkStatus(): NetworkStatusState {
  // Browser online state with debouncing
  const [browserOnline, setBrowserOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  // Convex connection state
  const convexState = useConvexConnectionState();

  // Refs for debounce timers
  const onlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const offlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current);
      if (offlineTimerRef.current) clearTimeout(offlineTimerRef.current);
    };
  }, []);

  // Handle browser online event (with debounce)
  const handleOnline = useCallback(() => {
    // Clear any pending offline timer
    if (offlineTimerRef.current) {
      clearTimeout(offlineTimerRef.current);
      offlineTimerRef.current = null;
    }

    // Debounce online state change
    onlineTimerRef.current = setTimeout(() => {
      setBrowserOnline(true);
      onlineTimerRef.current = null;
    }, ONLINE_DEBOUNCE_MS);
  }, []);

  // Handle browser offline event (with shorter debounce)
  const handleOffline = useCallback(() => {
    // Clear any pending online timer
    if (onlineTimerRef.current) {
      clearTimeout(onlineTimerRef.current);
      onlineTimerRef.current = null;
    }

    // Debounce offline state change (shorter delay - more urgent)
    offlineTimerRef.current = setTimeout(() => {
      setBrowserOnline(false);
      offlineTimerRef.current = null;
    }, OFFLINE_DEBOUNCE_MS);
  }, []);

  // Subscribe to browser online/offline events
  useEffect(() => {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  // Derive network status from combined signals
  const networkStatus = useMemo((): NetworkStatusState => {
    const isConvexConnected = convexState.isWebSocketConnected;
    const hasEverConnected = convexState.hasEverConnected;
    const connectionRetries = convexState.connectionRetries;

    let status: NetworkStatus;

    if (!browserOnline) {
      // Browser reports offline - show offline banner
      status = "offline";
    } else if (isConvexConnected) {
      // Browser online and Convex connected - normal operation
      status = "connected";
    } else if (hasEverConnected) {
      // Browser online, Convex disconnected, but has connected before
      // This is a reconnection scenario
      status = "reconnecting";
    } else {
      // First load - never connected to Convex yet
      // Let existing auth/loading UI handle this
      status = "loading";
    }

    return {
      status,
      isOnline: browserOnline,
      isConvexConnected,
      hasEverConnected,
      connectionRetries,
    };
  }, [browserOnline, convexState]);

  return networkStatus;
}
